"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  COOKIE_CONSENT_DEFAULTS,
  type CookieConsent,
  type CookieConsentList,
} from "../constants/cookie-consent";
import { consentToList } from "../helpers/cookie-utils";
import { updateConsent } from "../helpers/update-consent";

type CookieConsents = {
  consent: CookieConsent | null;
  consentList: CookieConsentList | null;
  deviceId: string | null;
  isReady: boolean;
  show: () => void;
  hide: () => void;
  submitConsent: (value: CookieConsent) => void;
};

const CookiebotContext = createContext<CookieConsents | null>(null);

type CookiebotProviderProps = React.PropsWithChildren<{
  // Useful for local + dev env setup, where cookiebot script is not supported
  sneakyMode?: boolean;
}>;

export function CookiebotProvider({
  children,
  sneakyMode = false,
}: CookiebotProviderProps) {
  const readyRef = useRef<Promise<void> | null>(null);

  const [isReady, setIsReady] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [consent, setConsent] = useState<CookieConsent | null>(null);

  useEffect(() => {
    let callback: () => void = () => {};

    // wrap the callback in a promise, so consumers can wait for ready.
    // also ensure that value gets updated on each call of consent ready.
    readyRef.current = new Promise((resolve, reject) => {
      callback = () => {
        const value = sneakyMode
          ? COOKIE_CONSENT_DEFAULTS
          : window.Cookiebot?.consent;

        if (!value) return reject("Unable to get cookiebot consent value");

        setConsent(value);

        const storedDeviceId = updateConsent(value);
        setDeviceId(storedDeviceId);

        setIsReady(true);

        resolve();
      };

      window.addEventListener("CookiebotOnConsentReady", callback);
    });

    // a) if we are in sneaky mode, trigger the custom event to spoof the load
    // b) if the timing is off, and 'CookiebotOnConsentReady' has already fired, we re-trigger it manually
    if (sneakyMode || window.Cookiebot?.consent) {
      const customEvent = new CustomEvent("CookiebotOnConsentReady");
      window.dispatchEvent(customEvent);
    }

    // return a cleanup fn
    return () => {
      window.removeEventListener("CookiebotOnConsentReady", callback);
    };
  }, [sneakyMode]);

  const consentList = useMemo(() => consentToList(consent), [consent]);

  const submitConsent = useCallback(async (value: Partial<CookieConsent>) => {
    await readyRef.current;

    window.Cookiebot?.submitCustomConsent(
      value.preferences ?? false,
      value.statistics ?? false,
      value.marketing ?? false,
    );
  }, []);

  const show = useCallback(async () => {
    await readyRef.current;
    window.Cookiebot?.show();
  }, []);

  const hide = useCallback(async () => {
    await readyRef.current;
    window.Cookiebot?.show();
  }, []);

  return (
    <CookiebotContext.Provider
      value={{
        consent,
        consentList,
        deviceId,
        isReady,
        show,
        hide,
        submitConsent,
      }}
    >
      {children}
    </CookiebotContext.Provider>
  );
}

export function useCookiebot() {
  const ctx = useContext(CookiebotContext);

  if (!ctx) throw new Error("Must be used within Cookiebot context");

  return ctx;
}
